import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, X, Loader2, Tag, Edit2, GitMerge } from "lucide-react";

interface Category {
  id: string;
  name: string;
  type: "income" | "expense";
}

const DEFAULT_INCOME_CATEGORIES = ["Salary", "Freelance", "Investment", "Gift", "Other"];
const DEFAULT_EXPENSE_CATEGORIES = [
  "Housing", "Transportation", "Food", "Utilities", "Healthcare",
  "Entertainment", "Shopping", "Education", "Savings", "Other"
];

export const CategoryManager = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState("");
  const [activeTab, setActiveTab] = useState<"expense" | "income">("expense");
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toast } = useToast();

  // Rename state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameCategory, setRenameCategory] = useState<Category | null>(null);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Merge state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState<Category | null>(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [merging, setMerging] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [transactionCount, setTransactionCount] = useState(0);
  const [checkingTransactions, setCheckingTransactions] = useState(false);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name");

      if (error) throw error;
      setCategories((data || []).map(c => ({
        id: c.id,
        name: c.name,
        type: c.type as "income" | "expense"
      })));
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast({
        title: "Error",
        description: "Failed to load categories",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    const trimmedName = newCategory.trim();
    const defaults = activeTab === "income" ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES;
    
    if (defaults.some(d => d.toLowerCase() === trimmedName.toLowerCase())) {
      toast({
        title: "Already exists",
        description: "This is a default category",
        variant: "destructive",
      });
      return;
    }

    if (categories.some(c => c.type === activeTab && c.name.toLowerCase() === trimmedName.toLowerCase())) {
      toast({
        title: "Already exists",
        description: "This category already exists",
        variant: "destructive",
      });
      return;
    }

    setAdding(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("categories")
        .insert({
          user_id: user.id,
          name: trimmedName,
          type: activeTab,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          toast({
            title: "Already exists",
            description: "This category already exists (case-insensitive match)",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      const newCat: Category = {
        id: data.id,
        name: data.name,
        type: data.type as "income" | "expense"
      };
      setCategories([...categories, newCat]);
      setNewCategory("");
      toast({
        title: "Category added",
        description: `"${trimmedName}" has been added to ${activeTab} categories`,
      });
    } catch (error) {
      console.error("Error adding category:", error);
      toast({
        title: "Error",
        description: "Failed to add category",
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteClick = async (category: Category) => {
    setCategoryToDelete(category);
    setCheckingTransactions(true);
    setDeleteConfirmOpen(true);
    
    try {
      const { count, error } = await supabase
        .from("transactions")
        .select("*", { count: "exact", head: true })
        .eq("category", category.name);
      
      if (error) throw error;
      setTransactionCount(count || 0);
    } catch (error) {
      console.error("Error checking transactions:", error);
      setTransactionCount(0);
    } finally {
      setCheckingTransactions(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    setDeletingId(categoryToDelete.id);
    try {
      const { error } = await supabase
        .from("categories")
        .delete()
        .eq("id", categoryToDelete.id);

      if (error) throw error;

      setCategories(categories.filter(c => c.id !== categoryToDelete.id));
      toast({
        title: "Category deleted",
        description: `"${categoryToDelete.name}" has been removed. ${transactionCount > 0 ? `${transactionCount} transactions still have this category label.` : ''}`,
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleRenameClick = (category: Category) => {
    setRenameCategory(category);
    setNewName(category.name);
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = async () => {
    if (!renameCategory || !newName.trim()) return;
    
    const trimmedName = newName.trim();
    
    // Check for duplicates
    const allCats = [...categories.filter(c => c.type === renameCategory.type).map(c => c.name), 
                     ...(renameCategory.type === "income" ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES)];
    if (allCats.some(c => c.toLowerCase() === trimmedName.toLowerCase() && c.toLowerCase() !== renameCategory.name.toLowerCase())) {
      toast({
        title: "Already exists",
        description: "A category with this name already exists",
        variant: "destructive",
      });
      return;
    }
    
    setRenaming(true);
    try {
      // Update category name
      const { error: catError } = await supabase
        .from("categories")
        .update({ name: trimmedName })
        .eq("id", renameCategory.id);

      if (catError) throw catError;

      // Update all transactions with the old category name
      const { error: txError } = await supabase
        .from("transactions")
        .update({ category: trimmedName })
        .eq("category", renameCategory.name);

      if (txError) throw txError;

      // Update recurring transactions too
      await supabase
        .from("recurring_transactions")
        .update({ category: trimmedName })
        .eq("category", renameCategory.name);

      setCategories(categories.map(c => 
        c.id === renameCategory.id ? { ...c, name: trimmedName } : c
      ));
      
      toast({
        title: "Category renamed",
        description: `"${renameCategory.name}" → "${trimmedName}"`,
      });
      
      setRenameDialogOpen(false);
      setRenameCategory(null);
    } catch (error) {
      console.error("Error renaming category:", error);
      toast({
        title: "Error",
        description: "Failed to rename category",
        variant: "destructive",
      });
    } finally {
      setRenaming(false);
    }
  };

  const handleMergeClick = (category: Category) => {
    setMergeSource(category);
    setMergeTarget("");
    setMergeDialogOpen(true);
  };

  const handleMergeConfirm = async () => {
    if (!mergeSource || !mergeTarget) return;
    
    setMerging(true);
    try {
      // Update all transactions to use target category
      const { error: txError } = await supabase
        .from("transactions")
        .update({ category: mergeTarget })
        .eq("category", mergeSource.name);

      if (txError) throw txError;

      // Update recurring transactions too
      await supabase
        .from("recurring_transactions")
        .update({ category: mergeTarget })
        .eq("category", mergeSource.name);

      // Delete the source category
      const { error: catError } = await supabase
        .from("categories")
        .delete()
        .eq("id", mergeSource.id);

      if (catError) throw catError;

      setCategories(categories.filter(c => c.id !== mergeSource.id));
      
      toast({
        title: "Categories merged",
        description: `"${mergeSource.name}" merged into "${mergeTarget}"`,
      });
      
      setMergeDialogOpen(false);
      setMergeSource(null);
    } catch (error) {
      console.error("Error merging categories:", error);
      toast({
        title: "Error",
        description: "Failed to merge categories",
        variant: "destructive",
      });
    } finally {
      setMerging(false);
    }
  };

  const getDisplayCategories = (type: "income" | "expense") => {
    const defaults = type === "income" ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES;
    const custom = categories.filter(c => c.type === type);
    return { defaults, custom };
  };

  const getMergeTargets = (sourceCategory: Category) => {
    const defaults = sourceCategory.type === "income" ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES;
    const custom = categories.filter(c => c.type === sourceCategory.type && c.id !== sourceCategory.id).map(c => c.name);
    return [...defaults, ...custom].sort();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "expense" | "income")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="expense">Expense</TabsTrigger>
          <TabsTrigger value="income">Income</TabsTrigger>
        </TabsList>

        {(["expense", "income"] as const).map((type) => {
          const { defaults, custom } = getDisplayCategories(type);
          return (
            <TabsContent key={type} value={type} className="space-y-4">
              {/* Default Categories */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">Default categories</p>
                <div className="flex flex-wrap gap-2">
                  {defaults.map((cat) => (
                    <Badge key={cat} variant="secondary" className="text-xs">
                      <Tag className="h-3 w-3 mr-1" />
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Custom Categories */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  Custom categories {custom.length > 0 && `(${custom.length})`}
                </p>
                {custom.length === 0 ? (
                  <p className="text-xs text-muted-foreground/70 italic">No custom categories yet</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {custom.map((cat) => (
                      <Badge
                        key={cat.id}
                        variant="outline"
                        className="text-xs pr-1 gap-1 group"
                      >
                        <Tag className="h-3 w-3" />
                        {cat.name}
                        <div className="flex items-center gap-0.5 ml-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 opacity-50 hover:opacity-100 hover:bg-primary/10 rounded-full"
                            onClick={() => handleRenameClick(cat)}
                            title="Rename"
                          >
                            <Edit2 className="h-2.5 w-2.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 opacity-50 hover:opacity-100 hover:bg-primary/10 rounded-full"
                            onClick={() => handleMergeClick(cat)}
                            title="Merge into another"
                          >
                            <GitMerge className="h-2.5 w-2.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 opacity-50 hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground rounded-full"
                            onClick={() => handleDeleteClick(cat)}
                            disabled={deletingId === cat.id}
                            title="Delete"
                          >
                            {deletingId === cat.id ? (
                              <Loader2 className="h-2.5 w-2.5 animate-spin" />
                            ) : (
                              <X className="h-2.5 w-2.5" />
                            )}
                          </Button>
                        </div>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Category */}
              <div className="flex gap-2">
                <Input
                  placeholder={`Add ${type} category...`}
                  value={activeTab === type ? newCategory : ""}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCategory();
                    }
                  }}
                  className="text-sm"
                />
                <Button
                  size="sm"
                  onClick={handleAddCategory}
                  disabled={adding || !newCategory.trim()}
                >
                  {adding ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current name</Label>
              <p className="text-sm text-muted-foreground">{renameCategory?.name}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-name">New name</Label>
              <Input
                id="new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter new name..."
              />
            </div>
            <p className="text-xs text-muted-foreground">
              This will update all existing transactions with this category.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameConfirm} disabled={renaming || !newName.trim()}>
              {renaming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Merge "{mergeSource?.name}" into:</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target category" />
                </SelectTrigger>
                <SelectContent>
                  {mergeSource && getMergeTargets(mergeSource).map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              All transactions with "{mergeSource?.name}" will be updated to use the target category, 
              and "{mergeSource?.name}" will be deleted.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMergeConfirm} disabled={merging || !mergeTarget}>
              {merging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              {checkingTransactions ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking transactions...
                </span>
              ) : transactionCount > 0 ? (
                <>
                  <strong className="text-foreground">{transactionCount} transaction{transactionCount !== 1 ? 's' : ''}</strong> currently use this category. 
                  They will keep the category label "{categoryToDelete?.name}" but it won't appear in the category list anymore.
                </>
              ) : (
                <>No transactions use this category. It can be safely deleted.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
              disabled={checkingTransactions}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
